/*
 * Copyright (c) 2018 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from './Header.css';
import {Navbar, Nav, NavDropdown, MenuItem} from 'eui-components';
import {FormattedMessage, intlShape, injectIntl, defineMessages} from 'react-intl';
import {unauthenticatedOrEldap, userIsAuthenticated} from '../auth';
import {LinkContainer} from 'react-router-bootstrap';

import logo from '../oclc-logo.png';

const messages = defineMessages({
  imageAlt: {
    id: 'Header.img.alt',
    description: 'Alternate text to display when image is unavailable.',
    defaultMessage: 'logo'
  },

  helpMenu: {
    id: 'Header.needHelp',
    description: 'Help menu label',
    defaultMessage: 'Need Help?'
  }
});

/**
 * Renders the application header.
 */
class Header extends Component {
    renderWorldcatAccountLink = () => {
        const ManageAccountLink = unauthenticatedOrEldap(() =>
            [<MenuItem key={1} eventKey={1.1} href="https://www.worldcat.org" id="header-manage-worldcat-account-link"
                       target="_blank">
                <FormattedMessage id='Header.manageWorldCatAccount' defaultMessage='Manage WorldCat Account'/>
            </MenuItem>,
                <MenuItem divider key={2}/>
            ]
        );

        return (
            <ManageAccountLink />
        );
    };

    renderUserDropDown = () => {
      if (!this.props.user) {
        return null;
      }

      const href = `${process.env.PUBLIC_URL}/logout`;

      const LogoutLink = userIsAuthenticated(() =>
        <MenuItem eventKey={2.4} href={href} id='header-signout-link'>
          <FormattedMessage id='Header.signOut' defaultMessage='Sign out' />
        </MenuItem>
      )

      return (
        <NavDropdown eventKey={2} title={`${this.props.user.firstName} ${this.props.user.lastName}`} id="header-user-menu">
          <MenuItem eventKey={2.1} href="https://help.oclc.org/" target="_blank">
            <FormattedMessage id='Header.help.contactSupport' defaultMessage='Contact Support'/>
          </MenuItem>
          <LinkContainer to="/languages">
            <MenuItem eventKey={2.3} id='header-languages-link'>
              <FormattedMessage id='Header.languages' defaultMessage='Languages' />
            </MenuItem>
          </LinkContainer>
          <MenuItem divider/>
          <LogoutLink />
        </NavDropdown>
      );
    };

    renderSandboxWarning = () => {
        if (this.props.custSandboxEnv) {
            return (
                <div className={classnames.sandboxWarning}>
                    <span className={classnames.sandboxWarningBold}><FormattedMessage id='Header.sandboxWarning1' defaultMessage='WMS sandbox'/></span>
                    <span className={classnames.sandboxWarningText}><FormattedMessage id='Header.sandboxWarning2' defaultMessage=' - You are viewing your library’s WMS training and testing environment'/></span>
                </div>
            );
        }
        return null;
    };

    render () {
      return (
        <header id="sc-header">
          {this.renderSandboxWarning()}
          <Navbar id="sc-header-navbar" staticTop className={classnames.header} fluid>
            <Navbar.Header >
              <Navbar.Brand>
                <a href={process.env.PUBLIC_URL || '/'}>
                  <img src={logo} className={classnames.logo}
                    alt={this.props.intl.formatMessage(messages.imageAlt)}/>
                </a>
              </Navbar.Brand>
            </Navbar.Header>
            <Nav pullRight>
              <NavDropdown eventKey={1} title={this.props.intl.formatMessage(messages.helpMenu)}
                id="sc-help-menu">
                {this.renderWorldcatAccountLink()}
                <MenuItem eventKey={1.2} href="https://www.oclc.org/" target="_blank">
                  <FormattedMessage id='Header.help.oclcWorldwide' defaultMessage='OCLC Worldwide'/>
                </MenuItem>
                <MenuItem eventKey={1.3} href="https://www.oclc.org/webapp/wcs/stores/OCLC/index.jsp" target="_blank">
                  <FormattedMessage id='Header.help.onlineServiceCenter' defaultMessage='Online Service Center'/>
                </MenuItem>
                <MenuItem divider />
                <MenuItem eventKey={1.4} href="https://help.oclc.org/Librarian_Toolbox/OCLC_Service_Configuration" target="_blank">
                  <FormattedMessage id='Header.help' defaultMessage='Help'/>
                </MenuItem>
                <MenuItem eventKey={1.5} href="https://help.oclc.org/" target="_blank">
                  <FormattedMessage id='Header.help.contactSupport' defaultMessage='Contact Support'/>
                </MenuItem>
                <MenuItem divider />
                <MenuItem eventKey={1.6} href="https://www.oclc.org/en/policies/trademarks.html" target="_blank">
                  <FormattedMessage id='Header.help.trademarks' defaultMessage='Trademarks'/>
                </MenuItem>
                <MenuItem eventKey={1.7} href="https://www.oclc.org/en/policies/privacy.html" target="_blank">
                  <FormattedMessage id='Header.help.privacy' defaultMessage='Privacy Policy'/>
                </MenuItem>
                <MenuItem eventKey={1.8} href="https://www.oclc.org/us/en/policies/copyright/default.htm" target="_blank">
                  <FormattedMessage id='Header.help.copyright' defaultMessage='Copyright Policy'/>
                </MenuItem>
              </NavDropdown>
              {this.renderUserDropDown()}
            </Nav>
          </Navbar>
        </header>
      );
    }
}

Header.propTypes = {
  intl: intlShape.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string
  })
};

export default injectIntl(Header);



// WEBPACK FOOTER //
// ./src/Header/Header.js